import { useEffect, useRef } from 'react';
import { trackScrollDepth } from '../utils/tagManager';

/**
 * Hook pour suivre la profondeur de défilement
 * @param {Object} options - Options de configuration
 * @param {Array<number>} options.thresholds - Seuils de défilement en pourcentage (défaut: [25, 50, 75, 90, 100])
 * @param {boolean} options.trackSections - Suivre les sections visibles (défaut: true)
 */
const useScrollTracking = (options = {}) => {
  const {
    thresholds = [25, 50, 75, 90, 100],
    trackSections = true
  } = options || {};

  // Référence pour stocker les seuils déjà atteints
  const reachedThresholds = useRef(new Set());
  // Référence pour stocker les sections déjà vues
  const viewedSections = useRef(new Set());

  useEffect(() => {
    // Fonction pour calculer la profondeur de défilement
    const calculateScrollDepth = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Calcul du pourcentage de défilement
      const scrollPercentage = Math.floor((scrollTop + windowHeight) / documentHeight * 100);
      
      return scrollPercentage;
    };

    // Fonction pour suivre les seuils de défilement
    const trackThresholds = () => {
      const currentScrollDepth = calculateScrollDepth();
      
      // Vérifier chaque seuil
      thresholds.forEach(threshold => {
        if (currentScrollDepth >= threshold && !reachedThresholds.current.has(threshold)) {
          // Marquer ce seuil comme atteint
          reachedThresholds.current.add(threshold);
          
          // Envoyer l'événement de suivi
          trackScrollDepth(threshold, `${threshold}% de la page`);
        }
      });
    };

    // Fonction pour suivre les sections visibles
    const trackVisibleSections = () => {
      if (!trackSections) return;
      
      // Sélectionner toutes les sections avec un ID
      const sections = document.querySelectorAll('section[id], div[id].section, [data-section-id]');
      
      sections.forEach(section => {
        const sectionId = section.id || section.dataset.sectionId;
        if (!sectionId) return;
        
        // Vérifier si la section est visible
        const rect = section.getBoundingClientRect();
        const isVisible = (
          rect.top <= window.innerHeight / 2 && 
          rect.bottom >= window.innerHeight / 2
        );
        
        if (isVisible && !viewedSections.current.has(sectionId)) {
          // Marquer cette section comme vue
          viewedSections.current.add(sectionId);
          
          // Envoyer l'événement de suivi
          trackScrollDepth(calculateScrollDepth(), sectionId);
        }
      });
    };

    // Fonction pour gérer le défilement
    const handleScroll = () => {
      // Utiliser requestAnimationFrame pour limiter les appels
      window.requestAnimationFrame(() => {
        trackThresholds();
        trackVisibleSections();
      });
    };

    // Ajouter l'écouteur d'événement pour le défilement
    window.addEventListener('scroll', handleScroll);
    
    // Vérifier immédiatement au chargement de la page
    handleScroll();

    // Nettoyage lors du démontage du composant
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [thresholds, trackSections]);
};

export default useScrollTracking;